import React, { useMemo } from 'react';
import { ArrowRight, FileSpreadsheet, RefreshCw, UploadCloud } from 'lucide-react';
import Button from '../components/ui/Button';
import MetricCard from '../components/analytics/MetricCard';
import AISummary from '../components/analytics/AISummary';
import AnalyticsChart from '../components/analytics/AnalyticsChart';
import { createAnalyticsSnapshot, getStoredDataset } from '../services/analytics';

const DashboardPage: React.FC = () => {
  const dataset = getStoredDataset();
  const snapshot = useMemo(() => (dataset ? createAnalyticsSnapshot(dataset) : null), [dataset]);

  if (!dataset || !snapshot) {
    return (
      <section className="page-stack" style={{ maxWidth: '980px' }}>
        <div className="premium-card" style={{ padding: '3rem', minHeight: '420px', display: 'grid', alignContent: 'center' }}>
          <span className="icon-tile" style={{ width: '56px', height: '56px', marginBottom: '1.5rem' }}>
            <FileSpreadsheet size={28} />
          </span>
          <h1 style={{ fontSize: '2.5rem', marginBottom: '0.85rem' }}>Upload data to start analyzing</h1>
          <p style={{ marginBottom: '1.75rem' }}>
            GabMic turns CSV and Excel business files into metrics, charts, summaries, and answers. The dashboard stays empty until real data is uploaded.
          </p>
          <a href="/upload">
            <Button variant="primary">
              <UploadCloud size={18} />
              Upload CSV or Excel
            </Button>
          </a>
        </div>
      </section>
    );
  }

  return (
    <div className="page-stack">
      <header className="page-header">
        <div>
          <h1 style={{ fontSize: '2.2rem', marginBottom: '0.55rem' }}>Your business at a glance</h1>
          <p className="page-kicker">Key metrics and insights from your uploaded data.</p>
          <span className="file-chip" style={{ marginTop: '1rem' }}>
            <FileSpreadsheet size={17} color="var(--gabmic-orange)" />
            {dataset.fileName}
            <span className="status-dot" />
          </span>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <a href="/upload"><Button variant="outline"><RefreshCw size={17} />Replace data</Button></a>
          <a href="/analytics"><Button variant="primary">Open analytics<ArrowRight size={17} /></Button></a>
        </div>
      </header>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
        {snapshot.metrics.map((metric) => (
          <MetricCard key={metric.label} metric={metric} />
        ))}
      </div>

      <AISummary summary={snapshot.summary} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1rem' }}>
        <AnalyticsChart
          title="Trend"
          description="Monthly totals from the detected date and metric columns."
          data={snapshot.timeSeries}
          metric={snapshot.metricColumn}
          currency={snapshot.currency}
        />
        <AnalyticsChart
          title="Top Categories"
          description="Top grouped totals from the detected category column."
          data={snapshot.categorySeries}
          metric={snapshot.metricColumn}
          currency={snapshot.currency}
        />
      </div>
    </div>
  );
};

export default DashboardPage;
